import { useMemo, useState, useEffect, useRef, type MouseEvent } from 'react'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import {
    ArrowDownLeftIcon,
    ArrowUpRightIcon,
    ClipboardDocumentIcon,
    DocumentArrowDownIcon,
} from '@heroicons/react/24/outline'


import { CustomTable } from '../../../components/customTable'
import { PagePreloader } from '../../../components/pagePreloader'
import { SmoothTransition } from '../../../components/pageTransition'
import {
    RenderDefaultActions,
    RenderImageWizard,
    RenderStatus,
} from '../common'
import { usePopup } from '../../../context/popupContext'
import { useNotificationBar } from '../../../context/notificationContext'
import { twElementStyles } from '../../../twElementStyles'
import ViewEpisodeDialog from './ViewEpisodeDialog'

import {
    getErrorResponse,
    getUpdatedSearchParams,
    parseSearchParams,
} from '../../../services/util'
import {
    useEpisodesData,
    useDeleteEpisode,
    useStatusEpisode,
    useGetEpisodData,
    useExportAllEpisodes,
} from 'hooks/episode'
import { renderWidgetChips } from 'components/label/RenderLabels'
import { ConflictDetailsDrawer } from 'components/ConflictDetailsDrawer'
import { useBulkLabelGenerationContext } from 'context/bulkLabelGenerationContext'
import { useActiveAIPrompt } from 'hooks/ai-settings'
import { SparklesIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'

async function copyTextToClipboard(text: string): Promise<boolean> {
    if (!navigator.clipboard?.writeText) {
        return false
    }
    try {
        await navigator.clipboard.writeText(text)
        return true
    } catch {
        return false
    }
}

const EpisodeList = () => {
    // dialog
    const [addUpdate, setAddUpdate] = useState(false)
    const [editData, setEditData] = useState<any>(null)
    const [isEditForm, setIsEditForm] = useState(false)
    const [openView, setOpenView] = useState(false)
    const [viewData, setViewData] = useState(null)

    const [deleteConflict, setDeleteConflict] = useState<{
        message: string
        details: string[]
    } | null>(null)

    const navigate = useNavigate()
    const location = useLocation()

    // popup/notification
    const popup = usePopup()
    const notification = useNotificationBar()

    // selection
    const rowSelectionState = useState<RowSelectionState>({})
    const [bulkResults, setBulkResults] = useState<any>(null)
    const [showBulkResults, setShowBulkResults] = useState(false)

    const [copiedVideoRowKey, setCopiedVideoRowKey] = useState<string | null>(null)
    const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        return () => {
            if (copyFeedbackTimeoutRef.current) {
                clearTimeout(copyFeedbackTimeoutRef.current)
            }
        }
    }, [])

    // API hooks
    const { mutate: deleteShow } = useDeleteEpisode()
    const { mutate: episodeStatus } = useStatusEpisode()
    const { startGeneration, isGenerating, currentJob } = useBulkLabelGenerationContext()
    const { data: activePrompt } = useActiveAIPrompt()

    //Filter Params
    let [searchParams, setSearchParams] = useSearchParams()
    let filterParams: any = parseSearchParams(searchParams, 'filter')

    // fetch data
    const { data, isLoading, refetch, isError,
        isSuccess,
        error,
        dataUpdatedAt } =
        useGetEpisodData(filterParams, {
            keepPreviousData: true,
        })

    const useFormHook = useForm({
        defaultValues: filterParams,
    })

    const responseData = useMemo(() => {
        if (!data?.data) return null

        return {
            ...data.data,
            results: Array.isArray(data.data.results) ? data.data.results : [],
        }
    }, [data])

    // -------------------------------
    //  OPEN EDIT / ADD DIALOG
    // -------------------------------
    const openAddUpdateDialog = (row: any, type: 'edit' | 'add' | 'view') => {
        if (type === 'edit' && row) {
            setEditData({ ...row })
            setIsEditForm(true)
            setAddUpdate(true)
            navigate(`/episodes/edit/${row.id}`)
        }
        if (type === 'add') {
            navigate('/episodes/create')
        }
        if (type === 'view') {
            const fullRow = responseData?.results?.find(
                (item) => item.id === row.id
            )
            setViewData(fullRow || null)
            setOpenView(true)
        }
    }

    // -------------------------------
    //  STATUS TOGGLE (MATCH RESOURCE MODULE)
    // -------------------------------
    const onStatusChange = (row: any) => {
        const nextStatus = row.status ? 0 : 1

        popup.open({
            title: 'Are you sure?',
            message: `Do you want to ${nextStatus ? 'Activate' : 'Deactivate'
                } ${row.name} Episode?`,
            type: 'confirm',

            onAction: (value: boolean) => {
                if (!value) return
                console.log(value)
                console.log(nextStatus)
                const payload = { status: Number(nextStatus) }

                episodeStatus(
                    {
                        id: row.id,
                        data: payload,
                    },
                    {
                        onSuccess: () => {
                            notification.push({
                                title: 'Success!',
                                message: `${row.name} status updated successfully!`,
                                type: 'success',
                            })
                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        },
                    }
                )
            },
        })
    }

    // -------------------------------
    //  DELETE HANDLER (MATCH RESOURCE MODULE)
    // -------------------------------
    const onDeleteAction = (row: any) => {

        popup.open({
            title: 'Are you sure ?',
            message: (
                <>
                    Do you want to delete <strong>{row?.name}</strong> episode? This cannot be undone.
                </>
            ),
            type: 'confirm',
            onAction: (value: boolean) => {
                console.log('Delete action confirmed: ' + value)
                if (!value) return
                deleteShow(
                    {
                        id: row?.id,
                    },
                    {
                        onSuccess: () => {
                            notification.push({
                                title: `Episode deleted !`,
                                message: `Episode ${row?.name} has been deleted successfully.`,
                                type: 'info',
                            })
                            refetch()
                        },
                        onError: (error: any) => {
                            const response = error?.response?.data || {}
                            const statusCode = response?.statusCode
                            const message =
                                response?.message || 'Something went wrong'
                            const details = Array.isArray(response?.details)
                                ? response.details
                                : []

                            if (statusCode === 409) {
                                notification.push({
                                    title: 'Cannot Delete',
                                    message: (
                                        <div className="space-y-2">
                                            <p className="text-xs leading-snug text-slate-700 dark:text-slate-200">
                                                {message}
                                            </p>
                                            <button
                                                type="button"
                                                className="text-xs font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                onClick={() =>
                                                    setDeleteConflict({
                                                        message,
                                                        details,
                                                    })
                                                }
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    ),
                                    type: 'error',
                                })
                                return
                            }

                            notification.push({
                                title: 'Error',
                                message,
                                type: 'error',
                            })
                        },
                    }
                )
            },
            confirmButtonText: 'Delete',
            confirmButtonClass: twElementStyles.twButtonElegantRed,
        })
    }

    // -------------------------------
    //  BULK LABEL GENERATION HANDLER
    // -------------------------------
    const handleBulkLabelGeneration = async () => {
        const selectedEpisodeIds = Object.keys(rowSelectionState[0] || {})

        if (selectedEpisodeIds.length === 0) {
            notification.push({
                title: 'Info',
                type: 'info',
                message: 'Please select at least one episode for bulk label generation.'
            })
            return
        }

        if (!activePrompt) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: 'No active AI prompt found. Please configure one in Settings.'
            })
            return
        }

        // Show confirmation popup
        popup.open({
            title: 'Confirm Bulk Label Generation',
            message: (
                <>
                    <p className="mb-2">
                        You are about to generate labels for <strong>{selectedEpisodeIds.length}</strong> episode(s).
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong>Note:</strong> If no transcript is available, labels will be generated based on episode title and summary.
                    </p>
                    <p className="mt-2">Are you sure you want to continue?</p>
                </>
            ),
            type: 'confirm',
            onAction: async (confirmed: boolean) => {
                if (!confirmed) return

                await executeBulkLabelGeneration(selectedEpisodeIds)
            },
            confirmButtonText: 'Generate Labels',
            confirmButtonClass: twElementStyles.twButtonElegant,
        })
    }

    const executeBulkLabelGeneration = async (selectedEpisodeIds: string[]) => {
        // Start generation in background using context
        startGeneration(selectedEpisodeIds, activePrompt.prompt)

        // Clear selection immediately
        rowSelectionState[1]({})
    }

    // Watch for job completion to refresh list
    useEffect(() => {
        if (currentJob?.status === 'completed') {
            // Refresh the episode list to show updated labels
            refetch()

            // Show results if available
            if (currentJob.result) {
                setBulkResults({ data: currentJob.result })
                setShowBulkResults(true)
            }
        }
    }, [currentJob?.status, currentJob?.result, refetch])

    // -------------------------------
    //  GENERAL ACTION HANDLER
    // -------------------------------
    const onAction = (actionType: string, row: any) => {
        // Row already contains id, name, etc.
        if (actionType === 'edit') openAddUpdateDialog(row, 'edit')
        if (actionType === 'delete') onDeleteAction(row)
        if (actionType === 'view') openAddUpdateDialog(row, 'view')
    }

    // -------------------------------
    //  TABLE COLUMNS  (MATCH RESOURCE STYLE)
    // -------------------------------
    const columns: ColumnDef<any>[] = [
        {
            id: 'select',
            header: ({ table }) => {
                const isAllSelected = table.getIsAllRowsSelected()
                const isSomeSelected = table.getIsSomeRowsSelected()

                return (
                    <input
                        type="checkbox"
                        checked={isAllSelected}
                        ref={(input) => {
                            if (input) {
                                input.indeterminate = !isAllSelected && isSomeSelected
                            }
                        }}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                )
            },
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
            ),
            enableSorting: false,
        },
        {
            accessorKey: 'id',
            header: 'ID',
            cell: ({ getValue }) => {
                const id = getValue() as string
                return (
                    <div className="font-mono text-xs">
                        {id}
                    </div>
                )
            },
            enableSorting: false,
        },
        {
            id: 'image',
            header: 'THUMBNAIL',
            accessorKey: 'thumbnail',
            cell: ({ row }) =>
                RenderImageWizard({
                    row,
                    accessorKey: 'thumbnail',
                    className: 'w-20 h-12 object-cover rounded overflow-hidden',
                }),
            enableSorting: false,
        },

        { accessorKey: 'name', header: 'NAME' },

        // {
        //     accessorKey: 'labels',
        //     header: 'LABELS',

        //     cell: ({ getValue }) => {
        //         const value = getValue()

        //         // Case 1: Already an array → join and show
        //         if (Array.isArray(value)) {
        //             return value.length ? renderWidgetChips(value) : '-'
        //         }

        //         // Case 2: String (possibly JSON string) → parse & show
        //         if (typeof value === 'string') {
        //             try {
        //                 const parsed = JSON.parse(value)
        //                 return Array.isArray(parsed) ? renderWidgetChips(parsed) : value
        //             } catch {
        //                 // simple string like "label1,label2"
        //                 return value
        //             }
        //         }

        //         return '-'
        //     },
        // },

        // {
        //     accessorKey: "short_summary",
        //     header: "SHORT SUMMARY",
        //     cell: ({ getValue }) => {
        //         const value = String(getValue() || "")

        //         return (
        //             <div className="w-[120px] truncate">
        //                 {value}
        //             </div>
        //         )
        //     },
        // },
        { accessorKey: "creator", header: "CREATOR" },

        {
            accessorKey: 'video_id',
            header: 'VIDEO ID',
            cell: ({ row, getValue }) => {
                const raw = getValue() as string | number | null | undefined
                const text =
                    raw != null && String(raw).trim() !== ''
                        ? String(raw)
                        : ''
                const rowKey = String(row.original?.id ?? '')

                if (!text) {
                    return (
                        <span className="font-mono text-xs text-gray-400 dark:text-gray-500">
                            -
                        </span>
                    )
                }

                const isCopied = copiedVideoRowKey === rowKey

                const onCopy = async (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const ok = await copyTextToClipboard(text)
                    if (!ok) return
                    if (copyFeedbackTimeoutRef.current) {
                        clearTimeout(copyFeedbackTimeoutRef.current)
                    }
                    setCopiedVideoRowKey(rowKey)
                    copyFeedbackTimeoutRef.current = setTimeout(() => {
                        setCopiedVideoRowKey(null)
                        copyFeedbackTimeoutRef.current = null
                    }, 1500)
                }

                return (
                    <button
                        type="button"
                        onClick={onCopy}
                        title="Copy video ID"
                        className="group inline-flex w-full max-w-[min(100%,14rem)] items-center gap-1.5 rounded px-1 py-0.5 text-left font-mono text-xs text-gray-900 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-100 dark:hover:bg-slate-700"
                    >
                        <span className="min-w-0 flex-1 truncate">{text}</span>
                        {isCopied ? (
                            <span className="shrink-0 text-[10px] font-medium text-green-600 dark:text-green-400">
                                Copied!
                            </span>
                        ) : (
                            <ClipboardDocumentIcon
                                className="h-3.5 w-3.5 shrink-0 text-gray-400 opacity-80 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300"
                                aria-hidden
                            />
                        )}
                    </button>
                )
            },
        },

        // { accessorKey: "templateId", header: "TEMPLATE" },


        {
            accessorKey: 'createdAt',
            header: 'CREATED DATE',
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null

                return value ? moment(value).format('LL') : '-'
            },
        },
        {
            accessorKey: 'updatedAt',
            header: 'PUBLISH DATE',
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null

                return value ? moment(value).format('LL') : '-'
            },
        },

        {
            id: 'status',
            header: 'STATUS',
            accessorFn: (row) => Number(row.status),
            cell: ({ row }) => RenderStatus(row, 'status', onStatusChange),
        },

        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) =>
                RenderDefaultActions(row, ['edit', 'delete', 'view'], onAction),
        },
    ]

    const mobileViewOptions = {
        mainTitle: {
            accessKey: 'category_name',
        },
    }

    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (name) {
                let updated = getUpdatedSearchParams(filterParams, values, name)
                if (Object.keys(updated)?.length) {
                    let sp = encodeURIComponent(JSON.stringify(updated))
                    setSearchParams({ filter: sp })
                } else {
                    setSearchParams({})
                }
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    const exportEpisodes = useExportAllEpisodes()
    const onExport = () => {
        exportEpisodes()
    }

    const additionalOptions = [
        {
            Icon: ArrowUpRightIcon,
            text: 'Export Episodes',
            onClick: onExport
        },
    ]

    const filterOptions = [
        {
            key: 'status',
            label: 'Status',
            options: [
                { display: 'All', value: '' },
                { display: 'Active', value: 1 },
                { display: 'Inactive', value: 0 },
            ],
        },
    ]

    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (!name) return

            let updated = getUpdatedSearchParams(filterParams, values, name)

            if (Object.keys(updated)?.length) {
                let sp = encodeURIComponent(JSON.stringify(updated))
                setSearchParams({ filter: sp })
            } else {
                setSearchParams({})
            }
        })

        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    // Sync dialog open state with URL: open modal only for edit routes.
    useEffect(() => {
        const isEdit = location.pathname.includes('/edit')
        if (isEdit) {
            // ensure add/edit dialog is open and in edit mode
            setIsEditForm(true)
            setAddUpdate(true)
        } else {
            // close local dialog for all non-edit routes (create is handled by separate route)
            setAddUpdate(false)
            setIsEditForm(false)
            setEditData(null)
        }
    }, [location.pathname])

    // NOTE: dialog visibility is controlled by `addUpdate` and URL

    // If data is loading, still allow the add/edit form to appear when URL indicates create/edit
    if (
        isLoading &&
        !location.pathname.endsWith('/create') &&
        !location.pathname.includes('/edit')
    )
        return <PagePreloader />

    return (
        <SmoothTransition>
            <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg shadow">

                <ViewEpisodeDialog
                    isOpen={openView}
                    setIsOpen={setOpenView}
                    episodeData={viewData}
                />

                <ConflictDetailsDrawer
                    isOpen={!!deleteConflict}
                    onClose={() => setDeleteConflict(null)}
                    title="Cannot Delete Episode"
                    description={deleteConflict?.message ?? ''}
                    details={deleteConflict?.details ?? []}
                />

                {/* Show a small indicator when the URL is /create or /:id/edit */}
                {location.pathname.endsWith('/create') && (
                    <div className="mb-4 text-sm font-medium">
                        Creating new episode
                    </div>
                )}
                {location.pathname.includes('/edit') && (
                    <div className="mb-4 text-sm font-medium">
                        Editing episode
                    </div>
                )}

                {/* Bulk Label Generation Button */}
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            {Object.keys(rowSelectionState[0] || {}).length} episodes selected
                        </span>
                    </div>
                    <button
                        onClick={handleBulkLabelGeneration}
                        disabled={Object.keys(rowSelectionState[0] || {}).length === 0 || isGenerating}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                        <SparklesIcon className="h-4 w-4" />
                        {isGenerating ? 'Generating...' : 'Generate Labels'}
                    </button>
                </div>

                <CustomTable
                    title="Episodes"
                    titleLabel={`Last updated ${moment(
                        dataUpdatedAt
                    ).fromNow()}`}
                    columns={columns}
                    data={responseData?.results}
                    pages={responseData?.pages}
                    totalRecords={responseData?.total}
                    additionalOptions={additionalOptions}
                    isLoading={isLoading}
                    isErrored={isError}
                    formHook={useFormHook}
                    rowSelectionState={rowSelectionState}
                    filterOptions={filterOptions} // <-- ADD THIS
                    onRefresh={refetch} // <-- match resource module
                    onAddAction={() => openAddUpdateDialog(null, 'add')}
                />

                {/* Bulk Results Modal */}
                {showBulkResults && bulkResults && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                        Bulk Label Generation Results
                                    </h3>
                                    <button
                                        onClick={() => setShowBulkResults(false)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>
                                <div className="mt-2">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                        {bulkResults.data?.successful_episodes}/{bulkResults.data?.total_episodes} episodes processed successfully
                                    </span>
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="space-y-4">
                                    {bulkResults.data?.results?.map((result: any, index: number) => (
                                        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                                        {result.episode_name}
                                                    </h4>
                                                    <div className="mt-2 flex items-center gap-2">
                                                        {result.status === 'success' ? (
                                                            <>
                                                                <CheckIcon className="h-4 w-4 text-green-500" />
                                                                <span className="text-sm text-green-600 dark:text-green-400">
                                                                    Success - {result.content_source}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <XMarkIcon className="h-4 w-4 text-red-500" />
                                                                <span className="text-sm text-red-600 dark:text-red-400">
                                                                    Failed - {result.error_message}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>

                                                    {result.status === 'success' && result.labels && (
                                                        <div className="mt-2">
                                                            <span className="text-sm text-gray-600 dark:text-gray-400">Labels: </span>
                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                {result.labels.map((label: string, labelIndex: number) => (
                                                                    <span key={labelIndex} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                                                                        {label}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                        Processing time: {result.processing_time}s
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </SmoothTransition>
    )
}

export default EpisodeList
